/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { useLocation } from 'react-router-dom';

import type { TypeActe, Mention, Registre } from '@/types';
import { ROLES_PARENTS_PAR_TYPE_ACTE } from '@/constants';
import { AssistantContext } from '@/contexts';
import type { AssistantContextType } from '@/contexts/AssistantContext';

interface AssistantContextProviderProps {
  children: React.ReactNode;
}

const AssistantContextProvider = ({
  children,
}: AssistantContextProviderProps) => {
  const location = useLocation();

  // Le type d'acte est transmis à l'assistant via le paramètre d'URL `type`
  // (ex. /assistant?type=naissance) depuis la modale de saisie.
  const typeFromUrl = React.useMemo(
    () =>
      (new URLSearchParams(location.search).get('type') as TypeActe | null) ||
      null,
    [location.search],
  );

  const [selectedTypeActe, setSelectedTypeActe] =
    React.useState<TypeActe | null>(typeFromUrl);
  const [data, setData] = React.useState<Record<string, any>>({});
  const [selectedRegistre, setSelectedRegistre] = React.useState<
    Partial<Registre> | undefined
  >();
  const [mentions, setMentions] = React.useState<Mention[]>([]);
  const [errors, setErrors] = React.useState<Record<string, any>>({});

  React.useEffect(() => {
    setSelectedTypeActe(typeFromUrl);
  }, [typeFromUrl]);

  /**
   * Préremplit la civilité des parents quand le type d'acte l'impose par le
   * rôle (reconnaissance : père / mère). Le champ est alors verrouillé dans
   * `StepParents`, donc l'agent ne peut pas le corriger : la valeur doit être
   * posée ici, sinon `formatSexe` renverrait « femme » par défaut pour le père.
   *
   * ⚠️ Écrit uniquement si la civilité est absente : ce `useEffect` se
   * redéclenche sur un assistant déjà monté (le type vient de `location.search`)
   * et écraserait sans ça une saisie en cours. Aucun effet pour un type d'acte
   * hors du registre — la naissance garde une civilité libre et vide.
   */
  React.useEffect(() => {
    const roles = typeFromUrl
      ? ROLES_PARENTS_PAR_TYPE_ACTE[typeFromUrl]
      : undefined;

    if (!roles) {
      return;
    }

    setData((prev) => {
      const manquants = ([1, 2] as const).filter((numero) =>
        _.isEmpty(prev?.[`parent${numero}`]?.sexe),
      );

      if (_.isEmpty(manquants)) {
        return prev;
      }

      const suivant = _.cloneDeep(prev);

      for (const numero of manquants) {
        _.set(suivant, `parent${numero}.sexe`, roles[numero].sexe);
      }

      return suivant;
    });
  }, [typeFromUrl]);

  const handleDataChange = React.useCallback(
    (name: string, value: string | number | boolean) => {
      setData((prev) => {
        const newData = _.cloneDeep(prev);

        _.set(newData, name, value);

        return newData;
      });
    },
    [],
  );

  const handleChange = React.useCallback(
    (_e, { name, value, checked }) => {
      handleDataChange(name, checked ?? value);
    },
    [handleDataChange],
  );

  const handleReset = React.useCallback(() => {
    setSelectedTypeActe(null);
    setData({});
    setSelectedRegistre(undefined);
    setMentions([]);
    setErrors({});
  }, []);

  const value = React.useMemo<AssistantContextType>(
    () => ({
      selectedTypeActe,
      setSelectedTypeActe,
      data,
      onChange: handleChange,
      selectedRegistre,
      setSelectedRegistre,
      mentions,
      setMentions,
      errors,
      setErrors,
      onReset: handleReset,
    }),
    [
      selectedTypeActe,
      data,
      handleChange,
      selectedRegistre,
      mentions,
      errors,
      handleReset,
    ],
  );

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
};

export default AssistantContextProvider;
